import { Divider, Flex, Group, ScrollArea, Stack, TextInput } from '@mantine/core';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { HEIGHT_BREAKPOINTS } from '../../../styles/breakpoints';
import { baseWhite, inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Checkbox } from '../../../ui-library/checkbox/checkbox';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { useEpicsSearch } from './initiative-modal.hooks';
import { InitiativeModalMode } from './initiative-modal.type';

type Props = {
  mode: InitiativeModalMode;
  selectedEpics: Epic[];
  setSelectedEpics: React.Dispatch<React.SetStateAction<Epic[]>>;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
};

export function StepTwo({ mode, selectedEpics, setSelectedEpics, onBack, onCancel, onSubmit, isSubmitting }: Props) {
  const i18n = useTranslation();
  const epics = useStrategyStore((state) => state.epics);
  const { height } = useWindowSize();
  const { filteredEpics, handleSearchChange } = useEpicsSearch(epics as Epic[]);

  const handleCheckboxChange = (epic: Epic, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedEpics((prev) => (event.target.checked ? [...prev, epic] : prev.filter((e) => e.id !== epic.id)));
  };

  return (
    <Stack mih={0}>
      <Text>{i18n.t('strategy.initiatives.form.epic_description')}</Text>

      <TextInput
        placeholder={i18n.t('strategy.initiatives.form.search_and_select_epics')}
        radius="xl"
        size="sm"
        leftSection={<Icon name="search" size={16} color={inkLight} />}
        onChange={(event) => {
          handleSearchChange(event.currentTarget.value);
        }}
      />

      <Divider />

      <ScrollArea type="always" h={height > parseInt(HEIGHT_BREAKPOINTS.md, 10) ? 336 : 240} scrollbarSize={8}>
        <Stack gap={24}>
          {filteredEpics.length === 0 ? (
            <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
              {i18n.t('strategy.filters.no_epics_found')}
            </Text>
          ) : (
            filteredEpics.map((epic) => (
              <Stack gap={8} key={epic.id}>
                <Checkbox
                  label={
                    <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                      {epic.title}
                    </Text>
                  }
                  checked={selectedEpics.some((e) => e.id === epic.id)}
                  onChange={(event) => handleCheckboxChange(epic, event)}
                />
                <Text size="small" style={{ width: 420 }}>
                  {epic.name}
                </Text>
              </Stack>
            ))
          )}
        </Stack>
      </ScrollArea>

      <Divider />

      <Flex justify="space-between" style={{ flexShrink: 0 }}>
        <Button
          variant="outline"
          radius="xl"
          onClick={onBack}
          leftSection={<Icon name="arrow_back" size={16} color={inkLight} pointer />}
        >
          {i18n.t('strategy.initiatives.form.back')}
        </Button>
        <Group gap={16}>
          <Button variant="outline" radius="xl" onClick={onCancel}>
            {i18n.t('strategy.initiatives.form.cancel')}
          </Button>
          <Button
            radius="xl"
            onClick={onSubmit}
            rightSection={<Icon name="task_alt" size={16} color={baseWhite} pointer />}
            loading={isSubmitting}
            loaderProps={{ size: 'xs' }}
          >
            {mode === InitiativeModalMode.Edit
              ? i18n.t('strategy.initiatives.form.save_changes')
              : i18n.t('strategy.initiatives.form.complete_and_create')}
          </Button>
        </Group>
      </Flex>
    </Stack>
  );
}
